import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAllCourses } from '../services/courseService';
import { getAllCategories } from '../services/categoryService';
import { resolveMediaUrl } from '../utils/media';
import './AllCourses.css';

function AllCourses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [courseModes, setCourseModes] = useState({});
  const [activeModalCourse, setActiveModalCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeStatus === 'completed' && getStatusCount('completed') === 0) {
      setActiveStatus('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, activeStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [coursesResponse, categoriesResponse] = await Promise.all([
        getAllCourses({ isActive: true }),
        getAllCategories({ isActive: true, sortBy: 'order', sortOrder: 'asc' })
      ]);

      if (coursesResponse.success) {
        setCourses(coursesResponse.data);
      }

      if (categoriesResponse.success) {
        setCategories([
          { _id: 'all', name: 'All Categories', slug: 'all' },
          ...categoriesResponse.data
        ]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const getStatusCount = (status) => {
    const visibleCourses = courses.filter(c => c.isActive !== false);
    if (status === 'all') return visibleCourses.length;
    if (status === 'upcoming') {
      return visibleCourses.filter(c => c.status === 'upcoming' || c.tags?.some(t => t.toLowerCase().includes('upcoming'))).length;
    }
    if (status === 'completed') {
      return visibleCourses.filter(c => c.status === 'completed' || c.tags?.some(t => t.toLowerCase().includes('completed'))).length;
    }
    return 0;
  };

  const getCourseCount = (categoryId) => {
    if (categoryId === 'all') return courses.length;
    return courses.filter(c => c.category?._id === categoryId).length;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const isOfflineAvailable = (course) => {
    return Boolean(course.isOfflineAvailable);
  };

  const getCourseMode = (courseId) => courseModes[courseId] || 'online';

  const toggleCourseMode = (courseId, mode, e) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    setCourseModes(prev => ({ ...prev, [courseId]: mode }));
  };

  const handleCardClick = (e, course) => {
    if (
      e.target.closest('.course-mode-selector') ||
      e.target.closest('.mode-pill') ||
      e.target.closest('.tc-link-btn') ||
      e.target.closest('.tc-modal-backdrop') ||
      e.target.closest('.tc-modal-card')
    ) {
      return;
    }
    const mode = getCourseMode(course._id);
    navigate(`/courses/${course.path}${mode === 'offline' ? '?mode=offline' : ''}`);
  };

  const getDisplayPricing = (course) => {
    const mode = getCourseMode(course._id);
    if (mode === 'offline' && isOfflineAvailable(course)) {
      const offlinePrice = course.offlinePrice || 10000;
      const offlineOrigPrice = course.offlineOriginalPrice || (course.offlinePrice ? course.offlinePrice : 15000);
      const discountPercent = offlineOrigPrice > offlinePrice
        ? Math.round(((offlineOrigPrice - offlinePrice) / offlineOrigPrice) * 100)
        : null;

      return {
        mode: 'offline',
        price: offlinePrice,
        originalPrice: offlineOrigPrice,
        discountPercent,
        discountLabel: discountPercent ? `UPTO ${discountPercent}% OFF` : null,
        isDiscounted: Boolean(discountPercent),
        tAndCLabel: '*Call for Discount Eligibility ℹ️'
      };
    }
    // Check if logged-in student has an approved custom online price for this course
    const userCustomOnline = (user && Array.isArray(user.customPricing))
      ? user.customPricing.find((p) => {
          const cid = typeof p.courseId === 'object' && p.courseId !== null ? p.courseId._id : p.courseId;
          return cid === course._id;
        })
      : null;

    if (userCustomOnline && typeof userCustomOnline.customPrice === 'number' && userCustomOnline.customPrice >= 0) {
      const customPrice = userCustomOnline.customPrice;
      const customDiscountPercent = course.price > customPrice
        ? Math.round(((course.price - customPrice) / course.price) * 100)
        : null;

      return {
        mode: 'online',
        price: customPrice,
        originalPrice: course.price,
        discountPercent: customDiscountPercent,
        discountLabel: customDiscountPercent ? `SCHOLARSHIP ${customDiscountPercent}% OFF` : '🏷️ CONCESSION APPLIED',
        isDiscounted: true,
        isCustomPrice: true,
        tAndCLabel: null
      };
    }

    const isDiscounted = course.originalPrice && course.originalPrice > course.price;
    const discountPercent = isDiscounted
      ? Math.round(((course.originalPrice - course.price) / course.originalPrice) * 100)
      : null;
    return {
      mode: 'online',
      price: course.price,
      originalPrice: course.originalPrice,
      discountPercent,
      discountLabel: discountPercent ? `UPTO ${discountPercent}% OFF` : null,
      isDiscounted,
      tAndCLabel: null
    };
  };

  const getCourseImage = (course) => resolveMediaUrl(course.image) || '/backend_dev_image.jpeg';

  const filteredCourses = courses
    .filter(course => course.isActive !== false)
    .filter(course => activeCategory === 'all' || course.category?._id === activeCategory)
    .filter(course => {
      if (activeStatus === 'all') return true;
      if (activeStatus === 'upcoming') {
        return course.status === 'upcoming' || course.tags?.some(t => t.toLowerCase().includes('upcoming'));
      }
      if (activeStatus === 'completed') {
        return course.status === 'completed' || course.tags?.some(t => t.toLowerCase().includes('completed'));
      }
      return true;
    })
    .filter(course => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        course.title?.toLowerCase().includes(q) ||
        course.instructor?.toLowerCase().includes(q) ||
        course.category?.name?.toLowerCase().includes(q) ||
        course.tags?.some(t => t.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => {
      if (sortBy === 'rating') {
        return (b.rating || 0) - (a.rating || 0);
      }
      if (sortBy === 'price-low') {
        return (a.price || 0) - (b.price || 0);
      }
      if (sortBy === 'price-high') {
        return (b.price || 0) - (a.price || 0);
      }
      return 0;
    });

  if (loading) {
    return (
      <div className="courses-page-wrapper">
        <section className="courses-hero-section">
          <div className="container">
            <div className="courses-hero-content">
              <span className="courses-eyebrow">COURSES</span>
              <h1 className="courses-hero-heading">Explore Our Courses</h1>
              <p className="courses-hero-desc">Loading available cohorts...</p>
            </div>
          </div>
        </section>
        <div className="container" style={{ padding: '60px 0', textAlign: 'center' }}>
          <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="courses-page-wrapper">
        <section className="courses-hero-section">
          <div className="container">
            <div className="courses-hero-content">
              <span className="courses-eyebrow">COURSES</span>
              <h1 className="courses-hero-heading">Explore Our Courses</h1>
              <p className="courses-hero-desc error-message" style={{ color: 'var(--danger-600)' }}>{error}</p>
              <button className="btn btn-primary btn-sm" onClick={fetchData} style={{ marginTop: '16px' }}>
                Try Again
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="courses-page-wrapper">
      {/* Courses Page Hero */}
      <section className="courses-hero-section">
        <div className="container">
          <div className="courses-hero-content">
            <span className="courses-eyebrow">COURSES</span>
            <h1 className="courses-hero-heading">Explore Our Courses</h1>
            <p className="courses-hero-desc">
              Learn programming and software engineering through practical, structured courses designed for developers.
            </p>
          </div>
        </div>
      </section>

      {/* Discovery Interface */}
      <section className="course-discovery-section">
        <div className="container">
          {/* Controls Card */}
          <div className="discovery-controls-card">
            {/* Top row: Search & Sort */}
            <div className="discovery-top-row">
              <div className="discovery-search-wrap">
                <span className="search-icon-inside">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search courses by title, instructor, tech..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="discovery-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="discovery-clear-btn"
                    onClick={() => setSearchQuery('')}
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="discovery-sort-wrap">
                <label className="sort-label">Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="discovery-sort-select"
                >
                  <option value="featured">Featured First</option>
                  <option value="rating">Highest Rated</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>

            {/* Status Tabs (All, Upcoming, Completed) */}
            <div className="discovery-status-row">
              <div className="status-pills">
                <button
                  type="button"
                  className={`status-pill-btn ${activeStatus === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveStatus('all')}
                >
                  <span>All Courses</span>
                  <span className="status-pill-count">{getStatusCount('all')}</span>
                </button>
                <button
                  type="button"
                  className={`status-pill-btn ${activeStatus === 'upcoming' ? 'active' : ''}`}
                  onClick={() => setActiveStatus('upcoming')}
                >
                  <span>Upcoming Cohorts</span>
                  <span className="status-pill-count">{getStatusCount('upcoming')}</span>
                </button>
                {getStatusCount('completed') > 0 && (
                  <button
                    type="button"
                    className={`status-pill-btn ${activeStatus === 'completed' ? 'active' : ''}`}
                    onClick={() => setActiveStatus('completed')}
                  >
                    <span>Completed</span>
                    <span className="status-pill-count">{getStatusCount('completed')}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter Pills */}
            {categories.length > 0 && (
              <div className="discovery-categories-row">
                {categories.map(category => (
                  <button
                    key={category._id}
                    type="button"
                    className={`cat-filter-btn ${activeCategory === category._id ? 'active' : ''}`}
                    onClick={() => setActiveCategory(category._id)}
                  >
                    {category.name}
                    <span className="cat-count-badge">{getCourseCount(category._id)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Results Info */}
          <div className="discovery-results-bar">
            <span className="results-count-text">
              Showing <strong>{filteredCourses.length}</strong> {filteredCourses.length === 1 ? 'course' : 'courses'}
              {activeStatus !== 'all' && ` (${activeStatus === 'upcoming' ? 'Upcoming' : 'Completed'})`}
              {searchQuery && ` for "${searchQuery}"`}
            </span>
          </div>

          {/* Courses Grid */}
          <div className="courses-grid" id="courses-grid">
            {filteredCourses.length > 0 ? (
              filteredCourses.map(course => {
                const pricing = getDisplayPricing(course);
                const isOffline = isOfflineAvailable(course);
                const currentMode = getCourseMode(course._id);

                return (
                  <article
                    key={course._id}
                    className="course-card"
                    onClick={(e) => handleCardClick(e, course)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="course-thumb-wrap">
                      <img
                        src={getCourseImage(course)}
                        alt={course.title}
                        className="course-image"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.src = '/backend_dev_image.jpeg'; }}
                      />
                      <div className="course-thumb-overlay"></div>
                      <span className="course-badge badge-live">
                        {isOffline && currentMode === 'offline' ? 'Classroom Cohort' : 'Live Cohort'}
                      </span>
                      <div className="course-level-tag">
                        {course.level || 'Intermediate'}
                      </div>
                    </div>

                    <div className="course-card-body">
                      {/* Learning Mode Switcher */}
                      {isOffline && (
                        <div
                          className="course-mode-selector"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        >
                          <span className="mode-label">Mode:</span>
                          <div className="mode-pill-group">
                            <button
                              type="button"
                              className={`mode-pill ${currentMode === 'online' ? 'active' : ''}`}
                              onClick={(e) => toggleCourseMode(course._id, 'online', e)}
                            >
                              🌐 Online
                            </button>
                            <button
                              type="button"
                              className={`mode-pill ${currentMode === 'offline' ? 'active' : ''}`}
                              onClick={(e) => toggleCourseMode(course._id, 'offline', e)}
                            >
                              🏢 Offline
                            </button>
                          </div>
                        </div>
                      )}

                      {isOffline && currentMode === 'offline' && (
                        <div className="course-venue-tag">
                          <span>📍 Center: Jaipur Campus</span>
                        </div>
                      )}

                      <div className="course-meta-top">
                        <span className="meta-item">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                          {course.duration || '8 Weeks'}
                        </span>
                        <span className="meta-item meta-rating">
                          ★ {course.rating || 4.9}
                        </span>
                        <span className="meta-seats-pill">30 seats max</span>
                      </div>

                      <h3 className="course-title">{course.title}</h3>
                      <p className="course-description">
                        {course.shortDescription || 'Master real-world distributed architectures with weekly mentor pull request reviews.'}
                      </p>

                      {/* Tech Tags */}
                      {course.tags && course.tags.length > 0 && (
                        <div className="course-tech-tags">
                          {course.tags.flatMap(t => t.split(',')).slice(0, 4).map((t, idx) => (
                            t.trim() && <span className="tech-tag" key={idx}>{t.trim()}</span>
                          ))}
                        </div>
                      )}

                      <div className="course-card-footer">
                        <div className="course-price-wrap">
                          <span className="current-price">{formatPrice(pricing.price)}</span>
                          {pricing.originalPrice && pricing.originalPrice > pricing.price && (
                            <span className="orig-price">{formatPrice(pricing.originalPrice)}</span>
                          )}
                          {pricing.discountLabel && (
                            <span className="discount-pill-tag">{pricing.discountLabel}</span>
                          )}
                        </div>

                        <div className="course-actions">
                          {pricing.tAndCLabel ? (
                            <button
                              type="button"
                              className="tc-link-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setActiveModalCourse(course);
                              }}
                            >
                              Inquiry ℹ️
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/courses/${course.path}${currentMode === 'offline' ? '?mode=offline' : ''}`);
                            }}
                          >
                            Explore &rarr;
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="no-courses-found" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                <h3>No courses found matching your criteria.</h3>
                <p style={{ marginTop: '8px' }}>Try adjusting your filters or search keywords.</p>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: '16px' }}
                  onClick={() => { setActiveCategory('all'); setActiveStatus('all'); setSearchQuery(''); }}
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Classroom Batch Modal */}
      {activeModalCourse && (
        <div className="tc-modal-backdrop" onClick={() => setActiveModalCourse(null)}>
          <div className="tc-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="tc-modal-header">
              <div className="tc-modal-title-group">
                <span className="tc-modal-kicker">Classroom Training Program</span>
                <h3 className="tc-modal-title">Offline Batch &amp; Discount Inquiry</h3>
                <p className="tc-modal-subtitle">
                  {activeModalCourse.title} • Mentor: {activeModalCourse.instructor}
                </p>
              </div>
              <button
                type="button"
                className="tc-modal-close"
                onClick={() => setActiveModalCourse(null)}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            <div className="tc-modal-body">
              <div className="discount-call-notice">
                <div className="notice-badge">Special Concession: UPTO 33% OFF</div>
                <div className="pricing-summary-row">
                  <span className="concession-price">Starting from ₹10,000</span>
                  <span className="standard-price">Standard Fee: ₹15,000</span>
                </div>
                <p className="notice-desc">
                  A special fee concession of <strong>UPTO 33%</strong> is available for classroom batches at our Jaipur campus.
                </p>
              </div>

              <div className="venue-info-box">
                <div className="venue-header">
                  <span className="venue-icon">📍</span>
                  <h4 className="venue-title">Classroom Training Venue</h4>
                </div>
                <p className="venue-address">
                  P.No. 18, Radha Vihar, Manchwa, Jaipur, Rajasthan 303706
                </p>
                <div className="venue-meta">
                  <span>🏢 In-Person Practical Labs</span>
                  <span>👨‍🏫 Direct Mentor Mentorship</span>
                </div>
              </div>

              <div className="inquiry-contact-card">
                <div className="contact-heading">Speak to Admissions Desk:</div>
                <p className="contact-subtext">
                  Call or message our counseling team now to verify your discount eligibility and confirm batch timings:
                </p>
                <div className="contact-actions-row">
                  <a href="tel:+917852034945" className="btn btn-secondary btn-sm">
                    📞 Call: +91 7852034945
                  </a>
                  <a
                    href={`https://wa.me/917852034945?text=${encodeURIComponent(`Hi Durbhasi Gurukulam, I am interested in the Offline Classroom Batch for ${activeModalCourse.title} and want to check my eligibility for the offline batch discount.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm"
                  >
                    💬 WhatsApp Desk
                  </a>
                </div>
              </div>
            </div>

            <div className="tc-modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setActiveModalCourse(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  const path = activeModalCourse.path;
                  setActiveModalCourse(null);
                  navigate(`/courses/${path}?mode=offline`);
                }}
              >
                View Full Course Details →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AllCourses;
